import useAuth from "hooks/useAuth";
import CommonFooterComponents from "layouts/CommonFooterComponents";
import { createContext, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";

export const UserFeedbackContext = createContext({});

export const UserFeedbackContextProvider = ({children}) => {

    const { t } = useTranslation();
    const [isOpenSuccessMessage, setIsOpenSuccessMessage] = useState(false);
    const [isOpenErrorMessage, setIsOpenErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("success.api.save");
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [alertMessage, setAlertMessage] = useState(null);
    const [openAlert, setOpenAlert]  = useState(false);
    const { logout } = useAuth();

    const setAndShowErrorMessage = (message) => {
        if(!message) {
            setOpenBackdrop(false);
            return;
        }

        if(message.response && message.response.data && message.response.data.error_code) {
            setErrorMessage(t(message.response.data.error_code));
        } else if(message.response && message.response.data && message.response.data.code) {
            if(message.response.data.code === "token_not_valid") {
                logout();
                return;
            }
            setErrorMessage(t(message.response.data.code));
        } else if(message.response && message.response.error_code) {
            setErrorMessage(t(message.response.error_code));
        } else if(message.response && message.response.data && message.response.data.error) {
            const detail = message.response.data.detail;
            setErrorMessage(detail && typeof detail === 'string' ? detail : t(message.response.data.error));
        } else if(message.response && message.response.data && typeof message.response.data === 'object') {
            // DRF validation errors: { field: [msg, ...], ... }
            const msgs = Object.values(message.response.data).flat().filter((v) => typeof v === 'string');
            if (msgs.length > 0) {
                setErrorMessage(msgs[0]);
                setIsOpenErrorMessage(true);
                setOpenBackdrop(false);
                return;
            }
        } else if(message.message) {
            setErrorMessage(t(message.message));
        } else if (message.response && message.response.status === 404) {
            setErrorMessage(t("api.error.notFound"));
        } else if (message.response && message.response.status === 401) {
            setErrorMessage(t("api.error.forbidden"));
        } else if (message.response && message.response.status === 500) {
            setErrorMessage(t("api.error.internalServerError"));
        }  else {
            setErrorMessage(t(message));
        }
        
        setIsOpenErrorMessage(true);
        setOpenBackdrop(false);
    }

    const setAndShowSuccessMessage = (message, options) => {
        setSuccessMessage(t(message, options));
        setIsOpenSuccessMessage(true);
        setOpenBackdrop(false);
    }

    const handleCloseBackdrop = () => {
        setOpenBackdrop(false);
    };
    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setIsOpenSuccessMessage(false);
        setIsOpenErrorMessage(false);
    };

    const feedbackContext = {setAndShowErrorMessage, setAndShowSuccessMessage, setOpenBackdrop, setSeverity, setAlertMessage, setOpenAlert};

    return (
        <UserFeedbackContext.Provider value={feedbackContext}>
            <Outlet />
            {children}
            <CommonFooterComponents
                handleCloseAlert={handleClose}
                handleCloseBackdrop={handleCloseBackdrop}
                successMessage={successMessage}
                errorMessage={errorMessage}
                showSuccessMessage={isOpenSuccessMessage}
                showErrorMessage={isOpenErrorMessage}
                showBackDrop={openBackdrop} />
        </UserFeedbackContext.Provider>
    )
}

export const useUserFeedbackContext = () => {
    const context = useContext(UserFeedbackContext);
    if (context === undefined) {
        throw new Error("useUserFeedbackContext must be used within a UserFeedbackContextProvider");
    }
    return context;
}